<template>
  <div class="glossary-view">
    <!-- Header Banner -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-gradient-dark border-0 shadow-lg text-white" style="background: linear-gradient(135deg, #121829 0%, #0a0d17 100%); border-left: 4px solid #3b82f6 !important; border-radius: 14px;">
          <div class="card-body p-3 p-md-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
                  <span class="badge badge-primary font-weight-bold px-3 py-1">
                    <i class="mdi mdi-book-open-page-variant mr-1"></i>
                    {{ isEn ? 'Knowledge Hub & Glossary' : 'Pusat Pengetahuan & Glosarium' }}
                  </span>
                  <span class="badge badge-outline-warning">
                    <i class="mdi mdi-lightning-bolt mr-1"></i>
                    {{ isEn ? 'Complete Gamification Guide' : 'Panduan Lengkap Gamifikasi' }}
                  </span>
                  <span class="badge badge-outline-success">
                    <i class="mdi mdi-shield-check mr-1"></i>
                    {{ isEn ? 'Gamiteam 2.0 Official Guide' : 'Panduan Resmi Gamiteam 2.0' }}
                  </span>
                </div>
                <h2 class="font-weight-bold mb-2 text-white">
                  {{ isEn ? 'Terminology Glossary & System Guide' : 'Glosarium Istilah & Panduan Sistem' }}
                </h2>
                <p class="text-muted mb-0" style="font-size: 14px; line-height: 1.6;">
                  {{ isEn
                    ? 'Comprehensive guide for all concepts, terminology, performance mechanics, mathematical formulas, and role workflows across the Gamiteam platform.'
                    : 'Panduan komprehensif seluruh konsep, terminologi, mekanisme kinerja, rumus matematis, serta alur kerja peran di platform Gamiteam.' }}
                </p>
              </div>
              <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
                <div class="d-flex flex-column flex-sm-row justify-content-lg-end" style="gap: 8px;">
                  <button class="btn btn-outline-info font-weight-bold" @click="activeMainTab = 'calculator'">
                    <i class="mdi mdi-calculator mr-1"></i> {{ isEn ? 'Formula Simulator' : 'Simulator Rumus' }}
                  </button>
                  <button class="btn btn-primary font-weight-bold" @click="printOrExport">
                    <i class="mdi mdi-printer mr-1"></i> {{ isEn ? 'Print Guide' : 'Cetak Panduan' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs: Glosarium, Alur Peran, Formula & Kalkulator -->
    <div class="row mb-4">
      <div class="col-12">
        <ul class="nav nav-pills custom-pills p-2 bg-dark rounded-lg border border-secondary" style="gap: 8px;">
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'glossary' }"
              @click.prevent="activeMainTab = 'glossary'"
            >
              <i class="mdi mdi-format-list-bulleted-type mr-1"></i>
              {{ isEn ? 'Terminology Glossary' : 'Kamus Istilah' }} ({{ filteredTerms.length }}/{{ terms.length }})
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'workflows' }"
              @click.prevent="activeMainTab = 'workflows'"
            >
              <i class="mdi mdi-transit-connection-variant mr-1"></i>
              {{ isEn ? 'Role Workflows & Guides' : 'Panduan Alur Peran' }}
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'calculator' }"
              @click.prevent="activeMainTab = 'calculator'"
            >
              <i class="mdi mdi-function-variant mr-1"></i>
              {{ isEn ? 'Formulas & Simulation Calculator' : 'Rumus & Kalkulator Simulasi' }}
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'faq' }"
              @click.prevent="activeMainTab = 'faq'"
            >
              <i class="mdi mdi-frequently-asked-questions mr-1"></i>
              {{ isEn ? 'FAQ & Best Practices' : 'Tanya Jawab & Praktik Terbaik' }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- TAB 1: GLOSARIUM ISTILAH -->
    <div v-if="activeMainTab === 'glossary'">
      <!-- Search & Category Filter Controls -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card bg-dark border-secondary p-3">
            <div class="row align-items-center">
              <div class="col-md-6 mb-3 mb-md-0">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text bg-secondary border-0 text-white">
                      <i class="mdi mdi-magnify"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control bg-secondary text-white border-0"
                    :placeholder="isEn ? 'Search terms, formulas, or examples (e.g. Gold, Shared Target, Redemption Rate, Gemini OCR)...' : 'Cari istilah, rumus, atau contoh (mis: Gold, Target Bersama, Rasio Penukaran, OCR Gemini)...'"
                  />
                  <div class="input-group-append" v-if="searchQuery">
                    <button class="btn btn-outline-light border-0" @click="searchQuery = ''">
                      <i class="mdi mdi-close"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6 text-md-right">
                <span class="text-muted small mr-2">{{ isEn ? 'Filter Category:' : 'Filter Kategori:' }}</span>
                <div class="d-inline-flex flex-wrap" style="gap: 4px;">
                  <button
                    v-for="cat in displayedCategories"
                    :key="cat.id"
                    class="btn btn-xs py-1 px-2 font-weight-bold rounded-pill"
                    :class="selectedCategory === cat.id ? 'btn-warning text-dark' : 'btn-outline-secondary text-light'"
                    @click="selectedCategory = cat.id"
                  >
                    {{ cat.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Metrics Summary -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Total Documented Terms' : 'Total Istilah Terdata' }}</div>
            <h3 class="text-primary font-weight-bold mb-0 mt-1">{{ terms.length }}</h3>
            <span class="small text-muted">{{ isEn ? 'Covering all modules' : 'Mencakup seluruh modul' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Gamification Pillars' : 'Kategori Gamifikasi' }}</div>
            <h3 class="text-warning font-weight-bold mb-0 mt-1">{{ categories.length - 1 }} {{ isEn ? 'Pillars' : 'Pilar' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Economy, Tasks, Rules, Targets' : 'Ekonomi, Tugas, Aturan, Target' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Integrated AI Engine' : 'Mekanisme AI Terintegrasi' }}</div>
            <h3 class="text-success font-weight-bold mb-0 mt-1">{{ isEn ? 'Gemini OCR' : 'OCR Gemini' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Automated proof verification' : 'Verifikasi otomatis bukti kerja' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'SOP Governance' : 'Tata Kelola SOP' }}</div>
            <h3 class="text-info font-weight-bold mb-0 mt-1">{{ isEn ? 'Audited' : 'Teraudit' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Fair penalty & compensation' : 'Penalti adil & pemulihan' }}</span>
          </div>
        </div>
      </div>

      <!-- Terms Grid -->
      <div class="row" v-if="filteredTerms.length > 0">
        <div
          v-for="term in filteredTerms"
          :key="term.id"
          class="col-lg-6 mb-4 stretch-card"
        >
          <div class="card bg-dark border-secondary shadow-sm term-card h-100">
            <div class="card-body p-4 d-flex flex-column justify-content-between">
              <div>
                <!-- Top Badge Row -->
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="badge" :class="getCategoryBadgeClass(term.category)">
                    {{ getCategoryLabel(term.category) }}
                  </span>
                  <span class="badge" :class="getImpactBadgeClass(term.impact)">
                    <i :class="getImpactIcon(term.impact)" class="mr-1" aria-hidden="true"></i>
                    {{ isEn ? (term.impactLabelEn || term.impactLabel) : term.impactLabel }}
                  </span>
                </div>

                <!-- Term Title -->
                <h4 class="text-white font-weight-bold mb-1 d-flex align-items-center">
                  <i class="mdi mr-2 text-warning" :class="term.icon" style="font-size: 20px;" aria-hidden="true"></i>
                  <span>{{ isEn ? (term.nameEn || term.name) : term.name }}</span>
                </h4>

                <!-- Short Definition -->
                <p class="text-light mt-2 mb-3" style="font-size: 14px; line-height: 1.6;">
                  {{ isEn ? (term.definitionEn || term.definition) : term.definition }}
                </p>

                <!-- Detailed Mechanics & Workplace Example -->
                <div class="bg-secondary p-3 rounded mb-3 text-white-50" style="background-color: #1a2030 !important; font-size: 13px;">
                  <div class="mb-1 text-warning font-weight-bold">
                    <i class="mdi mdi-briefcase-check mr-1" aria-hidden="true"></i> {{ isEn ? 'Workplace Application:' : 'Penerapan di Tempat Kerja:' }}
                  </div>
                  <div class="text-light">{{ isEn ? (term.workplaceExampleEn || term.workplaceExample) : term.workplaceExample }}</div>
                </div>

                <!-- Formula if present -->
                <div v-if="term.formula" class="p-2 mb-3 rounded border border-info bg-dark text-info font-monospace small">
                  <strong><i class="mdi mdi-function mr-1" aria-hidden="true"></i> {{ isEn ? 'Formula:' : 'Rumus:' }}</strong> <code>{{ isEn ? (term.formulaEn || term.formula) : term.formula }}</code>
                </div>
              </div>

              <!-- Actionable Role Tip Footer -->
              <div class="pt-3 border-top border-secondary d-flex justify-content-between align-items-center">
                <span class="small text-muted">
                  <i class="mdi mdi-lightbulb-on text-warning mr-1" aria-hidden="true"></i>
                  <strong>{{ isEn ? ('Tips for ' + (term.targetRoleEn || term.targetRole) + ':') : ('Tips ' + term.targetRole + ':') }}</strong> {{ isEn ? (term.tipEn || term.tip) : term.tip }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Search State -->
      <div class="row" v-else>
        <div class="col-12 text-center py-5">
          <div class="display-1 text-muted"><i class="mdi mdi-file-search-outline" aria-hidden="true"></i></div>
          <h4 class="text-white mt-3">{{ isEn ? 'No terms found' : 'Istilah tidak ditemukan' }}</h4>
          <p class="text-muted">{{ isEn ? ('No terms matched the keyword "' + searchQuery + '".') : ('Tidak ada istilah yang cocok dengan kata kunci "' + searchQuery + '".') }}</p>
          <button class="btn btn-outline-warning mt-2" @click="searchQuery = ''; selectedCategory = 'all'">
            {{ isEn ? 'Reset Search' : 'Atur Ulang Pencarian' }}
          </button>
        </div>
      </div>
    </div>

    <!-- TAB 2: PANDUAN ALUR PERAN (WORKFLOWS) -->
    <div v-if="activeMainTab === 'workflows'">
      <div class="row mb-4">
        <!-- Role Selector Pills -->
        <div class="col-12 text-center mb-4">
          <div class="btn-group flex-wrap" role="group">
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'owner' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'owner'"
            >
              <i class="mdi mdi-crown mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Workspace Owner (Executive)' : 'Pemilik Ruang Kerja (Eksekutif)' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'maker' ? 'btn-success text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'maker'"
            >
              <i class="mdi mdi-hammer-wrench mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Program Manager (HR & Team Lead)' : 'Manajer Program (HR & Ketua Tim)' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'player' ? 'btn-info text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'player'"
            >
              <i class="mdi mdi-account-star mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Team Member (Employee)' : 'Anggota Tim (Karyawan)' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'superadmin' ? 'btn-danger text-white' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'superadmin'"
            >
              <i class="mdi mdi-shield-crown mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Platform Operator (Super Admin)' : 'Operator Platform (Super Admin)' }}
            </button>
          </div>
        </div>

        <!-- Workflow Details Content -->
        <div class="col-12">
          <div class="card bg-dark p-4" :class="currentWorkflowData.cardClass">
            <div class="d-flex align-items-center mb-3">
              <i class="mdi display-4 mr-3" :class="[currentWorkflowData.icon, currentWorkflowData.titleClass]" aria-hidden="true"></i>
              <div>
                <h3 class="font-weight-bold mb-1" :class="currentWorkflowData.titleClass">
                  {{ isEn ? currentWorkflowData.titleEn : currentWorkflowData.titleId }}
                </h3>
                <p class="text-muted mb-0">
                  {{ isEn ? currentWorkflowData.subtitleEn : currentWorkflowData.subtitleId }}
                </p>
              </div>
            </div>

            <div class="timeline-steps mt-4">
              <div
                v-for="(step, sIdx) in currentWorkflowData.steps"
                :key="sIdx"
                class="step-card mb-3 p-3 bg-secondary rounded"
                :class="currentWorkflowData.stepClass"
              >
                <h5 class="text-white font-weight-bold">
                  <span class="badge mr-2" :class="currentWorkflowData.badgeClass">
                    {{ isEn ? ('Step ' + (sIdx + 1)) : ('Langkah ' + (sIdx + 1)) }}
                  </span>
                  {{ isEn ? step.titleEn : step.titleId }}
                </h5>
                <p class="text-muted mb-0 small">
                  {{ isEn ? step.descEn : step.descId }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 3: FORMULA & SIMULATOR KALKULATOR -->
    <div v-if="activeMainTab === 'calculator'">
      <div class="row">
        <!-- Interactive Simulator Widget -->
        <div class="col-lg-5 mb-4">
          <div class="card bg-dark border-primary p-4 h-100">
            <div class="d-flex align-items-center mb-3">
              <i class="mdi mdi-calculator-variant text-primary display-4 mr-3"></i>
              <div>
                <h4 class="text-white font-weight-bold mb-1">
                  {{ isEn ? 'Gamification Calculation Simulator' : 'Simulator Kalkulasi Gamifikasi' }}
                </h4>
                <p class="text-muted small mb-0">
                  {{ isEn ? 'Enter simulation values to view live engine formulas.' : 'Masukkan angka simulasi untuk melihat hasil rumus secara langsung.' }}
                </p>
              </div>
            </div>

            <hr class="border-secondary my-3" />

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Total Member EXP:' : 'Total EXP Anggota:' }}
              </label>
              <input type="number" v-model.number="calcExp" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Consecutive Daily Streak (Days):' : 'Konsistensi Berturut-turut (Hari):' }}
              </label>
              <input type="number" v-model.number="calcStreak" class="form-control bg-secondary text-white border-0" min="1" max="100" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Gold Earned This Month:' : 'Gold Diperoleh Bulan Ini:' }}
              </label>
              <input type="number" v-model.number="calcGoldEarned" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Gold Redeemed for Rewards:' : 'Gold Ditukarkan ke Hadiah:' }}
              </label>
              <input type="number" v-model.number="calcGoldRedeemed" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="bg-secondary p-3 rounded mt-3" style="background-color: #121828 !important;">
              <h6 class="text-warning font-weight-bold mb-2">
                {{ isEn ? 'System Calculation Results:' : 'Hasil Perhitungan Sistem:' }}
              </h6>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Member Level:' : 'Level Anggota:' }}</span>
                <strong class="text-white">Level {{ calculatedLevel }} ({{ currentTierName }})</strong>
              </div>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Consistency Multiplier:' : 'Pengali Konsistensi:' }}</span>
                <strong class="text-info">{{ calculatedStreakMultiplier }}x Bonus</strong>
              </div>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Contribution to Shared Target:' : 'Kontribusi ke Target Bersama:' }}</span>
                <strong class="text-danger font-weight-bold">{{ calculatedBossDamage.toLocaleString(isEn ? 'en-US' : 'id-ID') }} {{ isEn ? 'Pts' : 'Poin' }}</strong>
              </div>
              <div class="d-flex justify-content-between small text-light">
                <span>{{ isEn ? 'Gold Redemption Rate:' : 'Rasio Penukaran Gold:' }}</span>
                <strong :class="burnRateColor">{{ calculatedBurnRate }}% ({{ burnRateStatus }})</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Formula Reference Cards -->
        <div class="col-lg-7 mb-4">
          <div class="card bg-dark border-secondary p-4 h-100">
            <h4 class="text-white font-weight-bold mb-3 d-flex align-items-center">
              <i class="mdi mdi-book-open-outline text-warning mr-2"></i>
              {{ isEn ? 'System Mathematical Formula Reference' : 'Lembar Referensi Rumus Matematis Sistem' }}
            </h4>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-warning">{{ isEn ? '1. Level Up Threshold Formula' : '1. Rumus Ambang Batas Kenaikan Level' }}</strong>
                <span class="badge badge-outline-warning">{{ isEn ? 'Leveling' : 'Kenaikan Level' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Determines the EXP threshold required to advance to the next level.' : 'Menentukan ambang batas EXP yang dibutuhkan untuk naik ke level berikutnya.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>EXP_Level(N) = N × 100</code>
              </div>
            </div>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-danger">{{ isEn ? '2. Quarterly Shared Target Contribution Formula' : '2. Rumus Kontribusi Target Bersama Kuartalan' }}</strong>
                <span class="badge badge-outline-danger">{{ isEn ? 'Shared Target' : 'Target Bersama' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Each employee work contribution point is converted into direct progress toward the shared quarterly team target.' : 'Setiap poin kontribusi kerja anggota dikonversi menjadi capaian langsung terhadap target bersama tim.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>{{ isEn ? 'Target_Contribution = (EXP + (Gold × 2)) × (1 + (Streak × 0.05))' : 'Kontribusi_Target = (EXP + (Gold × 2)) × (1 + (Rentetan × 0.05))' }}</code>
              </div>
            </div>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-success">{{ isEn ? '3. Gold Redemption Rate Formula (Company Economy)' : '3. Rumus Rasio Penukaran Gold (Ekonomi Perusahaan)' }}</strong>
                <span class="badge badge-outline-success">{{ isEn ? 'Executive ROI' : 'ROI Eksekutif' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Evaluates corporate gold currency liquidity to maintain healthy and balanced reward expenditures.' : 'Mengevaluasi likuiditas koin emas perusahaan agar pengeluaran hadiah tetap seimbang dan sehat.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>{{ isEn ? 'Redemption_Rate = (Gold_Redeemed / Total_Gold_Minted) × 100%' : 'Rasio_Penukaran = (Gold_Ditukarkan / Total_Gold_Diterbitkan) × 100%' }}</code>
                <div class="text-muted small mt-1">
                  {{ isEn ? 'Healthy Zone: 40% - 75% | <30% (Hoarding Points) | >90% (High Utilization)' : 'Zona Sehat: 40% - 75% | <30% (Karyawan Menimbun Poin) | >90% (Penukaran Tinggi)' }}
                </div>
              </div>
            </div>

            <div class="formula-item p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-primary">{{ isEn ? '4. Seat Quota Utilization Formula' : '4. Rumus Utilisasi Kapasitas Kursi' }}</strong>
                <span class="badge badge-outline-primary">{{ isEn ? 'Licensing & Quota' : 'Lisensi & Kuota' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Employee seat utilization efficiency relative to paid license capacity.' : 'Efisiensi utilisasi kursi karyawan terdaftar dibandingkan kapasitas lisensi yang dibayar.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>{{ isEn ? 'Seat_Utilization = (Active_Employees / Total_Seat_Quota) × 100%' : 'Utilisasi_Kursi = (Karyawan_Aktif / Total_Kapasitas_Kursi) × 100%' }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 4: FAQ & BEST PRACTICES -->
    <div v-if="activeMainTab === 'faq'">
      <div class="row">
        <div class="col-lg-8 mx-auto">
          <div class="card bg-dark border-secondary p-4">
            <h4 class="text-white font-weight-bold mb-4 text-center">
              <i class="mdi mdi-frequently-asked-questions text-warning mr-2"></i>
              {{ isEn ? 'Frequently Asked Questions (FAQ) & Best Practices' : 'Pertanyaan yang Sering Diajukan (FAQ) & Praktik Terbaik' }}
            </h4>

            <div class="accordion" id="glossaryAccordion">
              <div
                v-for="(faq, idx) in faqs"
                :key="idx"
                class="card bg-secondary border-0 mb-3 rounded"
              >
                <div class="card-header p-3 cursor-pointer" @click="toggleFaq(idx)">
                  <h6 class="mb-0 text-white font-weight-bold d-flex justify-content-between align-items-center">
                    <span><i class="mdi mdi-help-circle-outline text-warning mr-2"></i> {{ isEn ? faq.qEn : faq.qId }}</span>
                    <i class="mdi" :class="expandedFaq === idx ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                  </h6>
                </div>
                <div v-if="expandedFaq === idx" class="card-body p-3 pt-0 text-light small border-top border-secondary">
                  <p class="mb-0" style="line-height: 1.6;">{{ isEn ? faq.aEn : faq.aId }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GlossaryView",
  data() {
    return {
      activeMainTab: "glossary", // glossary | workflows | calculator | faq
      searchQuery: "",
      selectedCategory: "all",
      selectedRoleWorkflow: "owner",
      expandedFaq: 0,

      // Calculator fields
      calcExp: 350,
      calcStreak: 5,
      calcGoldEarned: 500,
      calcGoldRedeemed: 250,

      categories: [
        { id: "all", labelId: "Semua", labelEn: "All" },
        { id: "currency", labelId: "Mata Uang & Poin", labelEn: "Currency & Points" },
        { id: "quest", labelId: "Tugas & Misi KPI", labelEn: "Tasks & KPI Objectives" },
        { id: "discipline", labelId: "Tata Tertib & Sanksi", labelEn: "Discipline & Sanctions" },
        { id: "raid", labelId: "Target Bersama & Templat", labelEn: "Shared Targets & Templates" },
        { id: "org", labelId: "Tim & Apresiasi", labelEn: "Teams & Recognition" },
        { id: "role", labelId: "Peran & Hak Akses", labelEn: "Roles & Permissions" },
        { id: "billing", labelId: "Lisensi & Tagihan", labelEn: "Billing & Licensing" },
      ],

      roleWorkflows: {
        owner: {
          icon: "mdi-crown",
          cardClass: "border-warning",
          titleClass: "text-warning",
          badgeClass: "badge-warning text-dark",
          stepClass: "border-left-warning",
          titleId: "Alur Kerja Utama Pemilik Ruang Kerja",
          titleEn: "Main Workflow for Workspace Owner",
          subtitleId: "Fokus: Efektivitas Insentif (ROI), Manajemen Kapasitas Kursi, dan Pengendalian Anggaran Poin.",
          subtitleEn: "Focus: Return on Investment (ROI), Seat Quota Allocation, and Financial Risk Safeguards.",
          steps: [
            {
              titleId: "Registrasi & Penyiapan Ruang Kerja Perusahaan",
              titleEn: "Registration & Company Workspace Setup",
              descId: "Pemilik mendaftarkan perusahaan, memilih paket langganan (Starter, Growth, Enterprise), dan mendapatkan alokasi kuota kursi karyawan awal.",
              descEn: "Workspace Owner registers the enterprise workspace, selects an onboarding plan (Starter, Growth, Enterprise), and claims initial seat quota."
            },
            {
              titleId: "Menentukan Valuasi Gold & Rasio Hadiah Finansial",
              titleEn: "Configure Gold Valuation & Financial Reward Liability",
              descId: "Menentukan rasio konversi 1 Gold = Rp 1.000 (atau sesuai anggaran perusahaan), dan menetapkan batas liabilitas maksimum hadiah bulanan agar keuangan terkendali.",
              descEn: "Establish currency conversion ratio (e.g. 1 Gold = IDR 1,000) and set monthly redemption liability ceilings to ensure budget predictability."
            },
            {
              titleId: "Mendelegasikan Manajer Program untuk Divisi",
              titleEn: "Appoint Program Managers for Company Divisions",
              descId: "Menunjuk manajer atau tim HR untuk menyusun aturan, KPI divisi, tata tertib, serta katalog hadiah di Toko Hadiah.",
              descEn: "Assign managers and HR leaders to construct division KPIs, SOP checklists, and curated Reward Shop catalogs."
            },
            {
              titleId: "Memantau Analitik Eksekutif & Rasio Penukaran Gold",
              titleEn: "Monitor Executive Analytics & Gold Redemption Rate",
              descId: "Melalui menu Analitik Eksekutif, Pemilik memantau rasio penukaran gold, skor kecepatan tim, kontributor teratas antar divisi, dan kesehatan ekonomi poin.",
              descEn: "Via Executive Analytics, monitor point redemption rates, team velocity, division performance heatmaps, and total reward liability health."
            },
            {
              titleId: "Ekspansi Kuota Kursi Tambahan",
              titleEn: "On-demand License Expansion (Add-on Seats)",
              descId: "Saat merekrut karyawan baru, Pemilik dapat langsung membeli kuota kursi tambahan secara instan lewat gerbang pembayaran tanpa perlu mengganti paket langganan tahunan.",
              descEn: "When onboarding new hires, purchase scalable add-on seats instantly via payment gateway without renegotiating annual contracts."
            }
          ]
        },
        maker: {
          icon: "mdi-hammer-wrench",
          cardClass: "border-success",
          titleClass: "text-success",
          badgeClass: "badge-success text-dark",
          stepClass: "border-left-success",
          titleId: "Alur Kerja Utama Manajer Program",
          titleEn: "Main Workflow for Program Manager",
          subtitleId: "Fokus: Perancangan Misi KPI, Disiplin Tata Tertib SOP, Verifikasi Bukti Kerja AI, dan Katalog Hadiah.",
          subtitleEn: "Focus: KPI Objective Blueprinting, SOP Disciplinary Framework, AI Vision OCR, and Submission Verifications.",
          steps: [
            {
              titleId: "Merancang Misi Harian & Target Mingguan",
              titleEn: "Design Daily Habits & Sprint Objectives",
              descId: "Membuat misi kerja operasional berulang (kebiasaan harian) dan target proyek mingguan dengan bobot EXP dan Gold proporsional.",
              descEn: "Construct daily operational habit loops and weekly sprint targets with calibrated EXP and Gold coin rewards."
            },
            {
              titleId: "Menerapkan Aturan Tata Tertib SOP",
              titleEn: "Establish Standard Operating Procedure (SOP) Consequences",
              descId: "Menyusun aturan penalti gold transparan atas kelalaian kerja (misal: keterlambatan, kesalahan input data, atau pelanggaran keselamatan kerja).",
              descEn: "Define transparent point consequences for operational lapses (e.g. late submissions, data entry mistakes, or missed standups)."
            },
            {
              titleId: "Menyediakan Misi Kompensasi (Pemulihan Penalti & Reputasi)",
              titleEn: "Deploy Recovery Tasks (Compensation Objectives)",
              descId: "Menyiapkan tugas perbaikan sehingga karyawan yang terkena sanksi dapat memulihkan saldo gold dan reputasi kerjanya.",
              descEn: "Provide constructive remediation tasks allowing penalized members to restore deducted gold and maintain high team morale."
            },
            {
              titleId: "Persetujuan Verifikasi Bukti Kerja dengan AI Gemini",
              titleEn: "Submission Approvals with Gemini AI Vision Verification",
              descId: "Memeriksa lampiran bukti kerja karyawan dengan bantuan OCR AI Gemini yang secara otomatis membaca teks foto dalam hitungan detik.",
              descEn: "Review Proof of Work deliverables accelerated by Gemini Vision OCR which automatically extracts verification telemetry."
            },
            {
              titleId: "Kurasi Katalog Hadiah di Toko Hadiah",
              titleEn: "Curate Reward Shop Merchandise & Vouchers",
              descId: "Menambahkan hadiah fisik atau voucher digital yang dapat ditukar karyawan menggunakan akumulasi koin emas yang mereka kumpulkan.",
              descEn: "Stock meaningful physical rewards, experiences, and digital vouchers for employees to redeem with their accumulated gold."
            }
          ]
        },
        player: {
          icon: "mdi-account-star",
          cardClass: "border-info",
          titleClass: "text-info",
          badgeClass: "badge-info text-dark",
          stepClass: "border-left-info",
          titleId: "Alur Perjalanan Anggota Tim",
          titleEn: "Team Member Journey (Employee Experience)",
          subtitleId: "Fokus: Rutinitas Misi Harian, Konsistensi Rentetan, Kontribusi Target Bersama, dan Penukaran Hadiah.",
          subtitleEn: "Focus: Daily Habit Loops, Streak Consistency, Shared Target Contributions, and Reward Redemptions.",
          steps: [
            {
              titleId: "Masuk Harian & Menjaga Pengali Konsistensi",
              titleEn: "Daily Check-in & Maintaining Consecutive Streak Multipliers",
              descId: "Membuka platform setiap pagi untuk mengecek misi prioritas dan mempertahankan rentetan konsistensi agar pengali bonus tetap aktif.",
              descEn: "Log in each workday morning to review priority tasks and extend consecutive streaks for heightened multiplier bonuses."
            },
            {
              titleId: "Eksekusi Pekerjaan & Kirim Bukti Kerja",
              titleEn: "Execute Tasks & Submit Verifiable Deliverables",
              descId: "Menyelesaikan tugas kerja nyata dan melampirkan tangkapan layar atau tautan dokumen hasil pekerjaan ke formulir pengajuan.",
              descEn: "Complete real-world operational deliverables and submit clear evidence links or photos for verification."
            },
            {
              titleId: "Berkontribusi pada Target Bersama Divisi",
              titleEn: "Contribute Progress to Quarterly Shared Team Targets",
              descId: "Setiap penyelesaian misi otomatis mengonversi kontribusi kerja anggota menjadi poin capaian pada Target Bersama divisi.",
              descEn: "Each approved task translates effort directly into progress points toward the shared quarterly team target."
            },
            {
              titleId: "Apresiasi Rekan Tim melalui Ruang Apresiasi",
              titleEn: "Peer-to-Peer Appreciation in Kudos Lounge",
              descId: "Mengirimkan apresiasi dan bonus koin kepada rekan kerja yang saling membantu menyelesaikan pekerjaan lintas departemen.",
              descEn: "Dispatch commendations and bonus recognition coins to colleagues who demonstrated exemplary support and teamwork."
            },
            {
              titleId: "Penukaran Hadiah di Toko Hadiah & Kotak Hadiah",
              titleEn: "Reward Redemption in Reward Shop & Reward Box",
              descId: "Menukarkan akumulasi koin emas dengan voucher makanan, saldo dompet digital, perangkat kerja, atau fasilitas ekstra kantor.",
              descEn: "Redeem hard-earned Gold Coins for food vouchers, e-wallet balances, office perks, or electronics."
            }
          ]
        },
        superadmin: {
          icon: "mdi-shield-check",
          cardClass: "border-danger",
          titleClass: "text-danger",
          badgeClass: "badge-danger text-white",
          stepClass: "border-left-danger",
          titleId: "Alur Kerja Utama Operator Platform",
          titleEn: "Platform Operator Journey (Super Admin Console)",
          subtitleId: "Fokus: Tata Kelola Multi-Perusahaan, Persetujuan Transaksi Finansial, Log Audit, dan Tolok Ukur Platform.",
          subtitleEn: "Focus: Multi-Tenant Governance, Billing Verifications, Security Audit Logs, and Platform Benchmarks.",
          steps: [
            {
              titleId: "Meninjau Prospek Masuk & Permintaan Demo",
              titleEn: "Review Inbound Inquiries & Enterprise Leads",
              descId: "Memeriksa formulir pendaftaran perusahaan yang masuk dari halaman publik, mengonfirmasi kualifikasi, dan mengonversi menjadi ruang kerja aktif.",
              descEn: "Evaluate enterprise demo registrations from the public landing page, qualify leads, and convert them to live workspaces."
            },
            {
              titleId: "Persetujuan Faktur Transfer Bank Manual",
              titleEn: "Verify & Approve Manual Bank Invoices",
              descId: "Memverifikasi bukti transfer pembayaran faktur lisensi paket atau tambahan kuota kursi sebelum kapasitas ditambahkan otomatis ke perusahaan.",
              descEn: "Verify bank transfer slips for annual subscriptions or add-on seat expansions before automated credential provisioning."
            },
            {
              titleId: "Pemantauan Log Audit & Keamanan Platform",
              titleEn: "Monitor Security Audit Logs & System Integrity",
              descId: "Memeriksa catatan aktivitas seluruh perusahaan untuk mendeteksi anomali, gangguan keamanan, atau ketidaksesuaian saldo poin.",
              descEn: "Inspect immutable audit trails across all tenant accounts to safeguard against unauthorized access or point irregularities."
            }
          ]
        }
      },

      terms: [
        {
          id: "gold",
          name: "Koin Emas (Gold)",
          nameEn: "Gold Coins",
          english: "Virtual Corporate Currency",
          category: "currency",
          icon: "mdi-circle-multiple",
          impact: "positive",
          impactLabel: "Mata Uang Utama",
          impactLabelEn: "Primary Currency",
          definition: "Mata uang virtual primer di platform Gamiteam yang diperoleh karyawan setelah berhasil menyelesaikan misi dan tugas kerja. Gold ini memiliki nilai riil yang dapat ditukarkan di Toko Hadiah dengan barang, voucher, atau apresiasi nyata.",
          definitionEn: "The primary virtual currency on the Gamiteam platform earned by completing workplace tasks. Gold coins hold tangible value and can be redeemed in the Reward Shop for real-world vouchers, gadgets, or perks.",
          workplaceExample: "Karyawan menyelesaikan KPI pembuatan modul perangkat lunak tepat waktu dan menerima 150 Gold, yang kemudian diakumulasikan untuk menukar voucher makan siang.",
          workplaceExampleEn: "A software engineer ships a sprint module on time, earning 150 Gold, which is saved toward an executive dining voucher.",
          formula: "Total_Gold = Σ(Gold_Misi) + Σ(Gold_Apresiasi) - Σ(Gold_Penalti) - Σ(Gold_Penukaran)",
          formulaEn: "Total_Gold = Σ(Gold_Task) + Σ(Gold_Kudos) - Σ(Gold_Penalty) - Σ(Gold_Redeem)",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Pastikan nilai gold seimbang dengan tingkat kesulitan misi agar tidak memicu inflasi koin di perusahaan.",
          tipEn: "Calibrate gold payouts with task difficulty to prevent virtual currency inflation within the company."
        },
        {
          id: "exp",
          name: "EXP (Poin Pengalaman)",
          nameEn: "EXP (Experience Points)",
          english: "Career Progression",
          category: "currency",
          icon: "mdi-lightning-bolt",
          impact: "positive",
          impactLabel: "Progresi Karir",
          impactLabelEn: "Career Progression",
          definition: "Poin pengalaman yang tidak pernah berkurang yang merefleksikan jam terbang, loyalitas, dan akumulasi kontribusi kerja karyawan sejak hari pertama bergabung.",
          definitionEn: "Non-depreciating progression points reflecting career tenure, skill accumulation, and lifetime contributions since day one.",
          workplaceExample: "Setiap laporan mingguan yang diserahkan memberikan 50 EXP. Ketika mencapai 1000 EXP, anggota naik pangkat dari 'Anggota Muda' menjadi 'Spesialis Senior'.",
          workplaceExampleEn: "Submitting weekly reports awards 50 EXP. Hitting 1,000 EXP unlocks promotion from 'Associate' to 'Senior Specialist'.",
          formula: "Level = Floor(EXP / 100) + 1",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "EXP tidak bisa hangus meskipun koin emas Anda sudah ditukarkan menjadi hadiah di Toko Hadiah.",
          tipEn: "EXP never expires or depletes, even after redeeming all your Gold Coins in the Reward Shop."
        },
        {
          id: "streak",
          name: "Rentetan Harian",
          nameEn: "Daily Streak",
          english: "Consecutive Consistency Multiplier",
          category: "currency",
          icon: "mdi-fire",
          impact: "positive",
          impactLabel: "Pengali Bonus",
          impactLabelEn: "Multiplier Bonus",
          definition: "Indikator konsistensi harian saat anggota masuk dan menyelesaikan setidaknya 1 aktivitas kerja setiap hari secara berturut-turut tanpa jeda.",
          definitionEn: "A consecutive daily activity tracker rewarding continuous workplace engagement and habit formation without missed days.",
          workplaceExample: "Konsisten masuk dan memperbarui status harian selama 14 hari menghasilkan pengali konsistensi 1.25x untuk seluruh perolehan EXP dan kontribusi Target Bersama.",
          workplaceExampleEn: "Checking in and completing tasks for 14 consecutive days awards a 1.25x multiplier to all incoming EXP and Shared Target progress.",
          formula: "Pengali = 1.0 + (Min(Rentetan, 30) × 0.05)",
          formulaEn: "Multiplier = 1.0 + (Min(Streak, 30) × 0.05)",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Jangan biarkan rentetan Anda terputus! 1 hari absen akan mengatur ulang hitungan rentetan kembali ke 0.",
          tipEn: "Maintain your momentum! Missing a single workday resets your active streak back to zero."
        },
        {
          id: "burn_rate",
          name: "Rasio Penukaran Gold",
          nameEn: "Gold Redemption Rate",
          english: "Currency Outflow Ratio",
          category: "currency",
          icon: "mdi-trending-down",
          impact: "system",
          impactLabel: "Metrik Finansial",
          impactLabelEn: "Financial Metric",
          definition: "Rasio perbandingan antara koin emas yang telah ditukarkan menjadi hadiah (aliran keluar) dibandingkan dengan total koin emas yang diterbitkan dari misi (aliran masuk).",
          definitionEn: "The ratio of redeemed gold (redemption outflow) to newly minted task reward gold (minting inflow).",
          workplaceExample: "Jika tim HR menerbitkan 10.000 Gold dan karyawan menukar hadiah senilai 6.000 Gold, maka Rasio Penukaran berada di angka ideal 60%.",
          workplaceExampleEn: "If 10,000 Gold is minted and employees redeem 6,000 Gold in rewards, the monthly redemption rate sits at a healthy 60%.",
          formula: "Rasio_Penukaran = (Gold_Ditukarkan / Total_Gold_Diterbitkan) × 100%",
          formulaEn: "Redemption_Rate = (Gold_Redeemed / Total_Gold_Minted) × 100%",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Pantau angka ini di Analitik Eksekutif. Jika di bawah 30%, buatlah katalog hadiah yang lebih menarik.",
          tipEn: "Monitor this metric in Executive Analytics. If below 30%, introduce more desirable items in the Reward Shop."
        },
        {
          id: "quest",
          name: "Misi Kerja (Target KPI)",
          nameEn: "Work Tasks (KPI Objectives)",
          english: "Performance Objectives",
          category: "quest",
          icon: "mdi-bullseye-arrow",
          impact: "positive",
          impactLabel: "Target Kerja",
          impactLabelEn: "Work Target",
          definition: "Tugas kerja terukur dengan kriteria penyelesaian jelas, batas waktu, dan imbalan berupa EXP dan Koin Emas yang dirancang oleh Manajer Program.",
          definitionEn: "Actionable workplace objectives with measurable completion criteria, deadlines, and calibrated EXP/Gold payouts designed by Program Managers.",
          workplaceExample: "Misi Harian: 'Membersihkan meja kerja dan memilah 3 surel prioritas' (20 Gold). Misi Kuartalan: 'Mencapai target penjualan Rp 100 Juta' (1.500 Gold).",
          workplaceExampleEn: "Daily Task: 'Resolve 3 priority tickets' (30 Gold). Quarterly Task: 'Hit quarterly sales quota of $50k' (1,500 Gold).",
          formula: "Imbalan = Gold_Dasar × Tingkat_Kesulitan (Mudah: 1x, Sedang: 2x, Sulit: 5x)",
          formulaEn: "Reward = Base_Gold × Difficulty_Tier (Easy: 1x, Medium: 2x, Hard: 5x)",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Gunakan deskripsi yang spesifik dan terukur agar tim tidak bingung saat mengirimkan bukti kerja.",
          tipEn: "Provide explicit deliverables and evidence guidelines so team members know exactly what is required."
        },
        {
          id: "habit_tracker",
          name: "Pelacak Kebiasaan Harian",
          nameEn: "Habit Tracker",
          english: "Micro-Behavior Loop",
          category: "quest",
          icon: "mdi-repeat",
          impact: "positive",
          impactLabel: "Rutinitas Mikro",
          impactLabelEn: "Micro Habit",
          definition: "Misi mikro yang berulang setiap hari atau setiap minggu untuk membentuk kebiasaan kerja positif (misalnya: hadir tepat waktu, rapat koordinasi pagi, kerapian 5S).",
          definitionEn: "Recurring daily or weekly micro-actions designed to reinforce positive operational habits (e.g. punctuality, daily standups).",
          workplaceExample: "Kebiasaan: 'Hadir sebelum pukul 08:30 WIB'. Anggota yang mencentang kebiasaan ini mendapatkan 10 Gold dan menjaga rentetan kedisiplinannya.",
          workplaceExampleEn: "Habit: 'Check in before 8:30 AM'. Ticking this daily habit awards 10 Gold and preserves streak consistency.",
          formula: "Bonus_Bulanan = 30_Hari_Selesai ? +200_Bonus_Gold : 0",
          formulaEn: "Monthly_Bonus = 30_Days_Completed ? +200_Bonus_Gold : 0",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Kebiasaan harian adalah cara terbaik menumbuhkan budaya kerja unggul secara organik tanpa paksaan.",
          tipEn: "Micro-habits are the most sustainable driver of organizational excellence without coercive management."
        },
        {
          id: "gemini_ocr",
          name: "OCR AI Gemini Vision",
          nameEn: "Gemini AI Vision OCR",
          english: "Automated Proof Verification",
          category: "quest",
          icon: "mdi-robot",
          impact: "system",
          impactLabel: "Teknologi AI",
          impactLabelEn: "AI Technology",
          definition: "Sistem kecerdasan buatan berbasis Google Gemini Vision yang memindai gambar tangkapan layar atau foto bukti kerja untuk mendeteksi keaslian dan kesesuaian kriteria.",
          definitionEn: "Multimodal AI engine powered by Google Gemini Vision that inspects uploaded deliverables, receipts, and screenshots to verify task authenticity.",
          workplaceExample: "Karyawan mengunggah tangkapan layar transaksi penutupan kontrak; AI Gemini membaca angka nominal, tanggal, dan nama staf secara otomatis dalam 2 detik.",
          workplaceExampleEn: "An employee uploads a sales agreement screenshot; Gemini OCR extracts invoice totals, dates, and sign-offs within 2 seconds.",
          formula: "Persetujuan_Otomatis = Kepercayaan_AI >= 0.85 && Kecocokan_Teks == BENAR",
          formulaEn: "Auto_Approval = AI_Confidence >= 0.85 && Text_Match == TRUE",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Aktifkan verifikasi AI Gemini pada misi berulang agar manajer tidak kelelahan memverifikasi bukti kerja manual satu per satu.",
          tipEn: "Enable Gemini AI verification on high-volume tasks to eliminate manual review bottlenecks for managers."
        },
        {
          id: "proof_of_work",
          name: "Bukti Kerja (Deliverable)",
          nameEn: "Proof of Work (PoW)",
          english: "Deliverable Evidence",
          category: "quest",
          icon: "mdi-camera-check",
          impact: "system",
          impactLabel: "Bukti Validasi",
          impactLabelEn: "Evidence Proof",
          definition: "Berkas lampiran berupa foto, berkas PDF, atau tautan repositori/dokumen yang wajib disertakan oleh anggota saat mengklaim penyelesaian misi.",
          definitionEn: "Mandatory supporting documentation (screenshots, code PR links, receipts) submitted by team members to validate task completion.",
          workplaceExample: "Tautan pull request GitHub atau foto nota pengiriman barang yang diunggah ke formulir klaim misi.",
          workplaceExampleEn: "A link to a merged GitHub pull request or shipping manifest uploaded during task submission.",
          formula: "N/A",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Unggah gambar yang jelas dan beresolusi cukup agar proses persetujuan berjalan instan tanpa revisi.",
          tipEn: "Upload high-resolution images and clear links to accelerate instant approval without revisions."
        },
        {
          id: "sop_consequence",
          name: "Sanksi Pelanggaran SOP",
          nameEn: "SOP Consequence (Sanctions)",
          english: "Disciplinary Penalty",
          category: "discipline",
          icon: "mdi-alert-outline",
          impact: "negative",
          impactLabel: "Penalti Poin",
          impactLabelEn: "Point Penalty",
          definition: "Pengurangan Koin Emas otomatis atau manual akibat ketidakpatuhan terhadap SOP kerja yang telah disepakati bersama di perusahaan.",
          definitionEn: "Calibrated deduction of Gold Coins resulting from standard operating procedure (SOP) non-compliance.",
          workplaceExample: "Terlambat mengirimkan laporan mingguan lebih dari batas jam 17:00 dikenai penalti -30 Gold.",
          workplaceExampleEn: "Submitting a weekly client summary past the 5:00 PM cutoff incurs a calibrated -30 Gold consequence.",
          formula: "Gold_Akhir = Max(0, Gold_Saat_Ini - Nominal_Penalti)",
          formulaEn: "Final_Gold = Max(0, Current_Gold - Penalty_Amount)",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Gunakan penalti sebagai instrumen edukasi, bukan untuk menghukum semata. Selalu sertakan alasan pelanggaran secara transparan.",
          tipEn: "Treat consequences as educational feedback rather than punishment. Always accompany deductions with constructive rationale."
        },
        {
          id: "direct_punishment",
          name: "Tindakan Disiplin Langsung",
          nameEn: "Direct Disciplinary Sanction",
          english: "Immediate Incident Sanction",
          category: "discipline",
          icon: "mdi-octagon-outline",
          impact: "negative",
          impactLabel: "Sanksi Berat",
          impactLabelEn: "Severe Sanction",
          definition: "Tindakan disiplin berat yang diterapkan langsung oleh manajer atas insiden fatal (misalnya kelalaian keamanan kerja, kebocoran data, atau mangkir tanpa kabar).",
          definitionEn: "Immediate executive disciplinary sanction applied following critical incidents (security breaches, data leaks, unexplained absenteeism).",
          workplaceExample: "Meninggalkan server produksi mati tanpa koordinasi dikenai penalti langsung -200 Gold dan pembekuan klaim hadiah selama 7 hari.",
          workplaceExampleEn: "Causing uncoordinated production downtime results in an immediate -200 Gold penalty and 7-day reward store suspension.",
          formula: "N/A",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Sanksi berat harus terdokumentasi dan dapat diaudit di menu Log Audit Sistem.",
          tipEn: "Ensure severe incidents are fully documented and auditable in the System Audit Logs menu."
        },
        {
          id: "compensation_quest",
          name: "Misi Kompensasi (Pemulihan Penalti & Reputasi Poin)",
          nameEn: "Recovery Task (Compensation Objective)",
          english: "Reparation Task",
          category: "discipline",
          icon: "mdi-hand-heart",
          impact: "positive",
          impactLabel: "Pemulihan Poin",
          impactLabelEn: "Point Recovery",
          definition: "Mekanisme pemulihan yang adil di mana anggota yang terkena penalti dapat mengambil tugas ekstra untuk menebus koin yang hilang dan mengembalikan reputasinya.",
          definitionEn: "A restorative mechanism enabling penalized employees to undertake extra service tasks to recoup lost gold and restore standings.",
          workplaceExample: "Karyawan yang terlambat mengambil misi kompensasi 'Merapikan inventaris kantor setelah jam kerja' untuk mendapatkan kembali 30 Gold yang hilang.",
          workplaceExampleEn: "An employee takes on an after-hours documentation audit task to recover 30 deducted Gold coins.",
          formula: "Selisih_Akhir = Gold_Penalti - Gold_Kompensasi_Diperoleh",
          formulaEn: "Net_Loss = Penalty_Gold - Compensation_Gold_Earned",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Gamiteam mengedepankan budaya perbaikan berkelanjutan. Jangan biarkan anggota merasa putus asa karena poin negatif.",
          tipEn: "Gamiteam fosters a growth mindset. Never leave a motivated employee trapped in negative point territory."
        },
        {
          id: "world_realm",
          name: "Templat Kinerja (Cetak Biru Divisi)",
          nameEn: "Performance Blueprint (Division Template)",
          english: "Operational Program Blueprint",
          category: "raid",
          icon: "mdi-earth",
          impact: "system",
          impactLabel: "Cetak Biru Kerja",
          impactLabelEn: "Program Blueprint",
          definition: "Kerangka kerja terpadu tempat seluruh anggota divisi perusahaan berkolaborasi, memvisualisasikan progres kerja bersama, dan menuntaskan target kuartalan.",
          definitionEn: "Integrated program blueprint where cross-functional division members collaborate, visualize team momentum, and achieve quarterly targets.",
          workplaceExample: "Templat Penjualan B2B di mana progres target pendapatan diperbarui secara langsung saat kontrak tercapai.",
          workplaceExampleEn: "A B2B Sales Growth template where revenue milestones update in real time as team targets are reached.",
          formula: "N/A",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Pilih templat yang selaras dengan kebutuhan operasional dan budaya industri perusahaan Anda di Bursa Templat.",
          tipEn: "Choose a template in the Template Marketplace that resonates with your company's operational goals."
        },
        {
          id: "boss_raid",
          name: "Target Bersama Kuartalan",
          nameEn: "Quarterly Shared Target",
          english: "Cooperative Quarterly Target",
          category: "raid",
          icon: "mdi-bullseye-arrow",
          impact: "positive",
          impactLabel: "Target Kolektif",
          impactLabelEn: "Collective Target",
          definition: "Sasaran utama perusahaan (misal: Target Omzet Kuartal 4) yang hanya bisa dicapai jika seluruh anggota tim bahu-membahu menyumbangkan poin kontribusi.",
          definitionEn: "A collective corporate milestone (e.g. Q4 Revenue Goal) achieved through distributed cross-team task completions.",
          workplaceExample: "Target 'Pencapaian Pendapatan Kuartal' membutuhkan 50.000 Poin. Setiap misi yang diselesaikan anggota menambah capaian target secara proporsional.",
          workplaceExampleEn: "The 'Q4 Revenue Milestone' requires 500,000 Points. Every verified task completed advances the shared target progress bar.",
          formula: "Sisa_Target = Total_Target - Σ(Kontribusi_Seluruh_Anggota)",
          formulaEn: "Target_Remaining = Target_Total - Σ(All_Member_Contributions)",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Atur Target Bersama setiap awal kuartal untuk memicu semangat kerja sama lintas divisi.",
          tipEn: "Launch Shared Targets at the beginning of each quarter to galvanize cross-departmental alignment."
        },
        {
          id: "damage_dealt",
          name: "Poin Kontribusi Target",
          nameEn: "Target Contribution Points",
          english: "Shared Target Contribution",
          category: "raid",
          icon: "mdi-trending-up",
          impact: "positive",
          impactLabel: "Kontribusi Tim",
          impactLabelEn: "Team Contribution",
          definition: "Besaran poin capaian yang disumbangkan anggota ke Target Bersama, dihitung dari kombinasi perolehan EXP, Gold, dan pengali konsistensi aktif.",
          definitionEn: "Quantified progress points contributed by a team member toward the shared target, derived from completed EXP, Gold, and active streak multipliers.",
          workplaceExample: "Menyelesaikan misi bernilai 100 EXP dengan rentetan 10 hari menghasilkan 250 Poin Kontribusi ke Target Kuartalan.",
          workplaceExampleEn: "Completing a 100 EXP task with a 10-day streak contributes 250 progress points toward the quarterly target.",
          formula: "Kontribusi = (EXP + (Gold × 2)) × Pengali_Rentetan",
          formulaEn: "Contribution = (EXP + (Gold × 2)) × Streak_Multiplier",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Semakin tinggi konsistensi dan semakin berbobot misi Anda, semakin besar kontribusi Anda bagi tim!",
          tipEn: "Higher streaks and high-impact tasks generate significantly amplified contributions toward team goals."
        },
        {
          id: "realm_loot_chest",
          name: "Dana Bonus Kolektif",
          nameEn: "Shared Bonus Pool",
          english: "Shared Target Bonus Pool",
          category: "raid",
          icon: "mdi-gift-outline",
          impact: "positive",
          impactLabel: "Hadiah Kolektif",
          impactLabelEn: "Shared Bonus",
          definition: "Alokasi bonus bersama yang dicairkan ketika Target Bersama berhasil dituntaskan, membagikan bonus koin emas ke seluruh anggota yang berpartisipasi.",
          definitionEn: "A shared reward pool that unlocks upon target completion, distributing proportional gold bonuses to contributing team members.",
          workplaceExample: "Ketika Target Kuartal 4 tercapai, Dana Bonus Kolektif membagikan bonus 500 Gold ke masing-masing karyawan yang menyumbang minimal 1.000 Poin.",
          workplaceExampleEn: "When the Q4 target is achieved, the Shared Bonus Pool distributes 500 bonus Gold to every contributor with at least 1,000 points.",
          formula: "Porsi_Individu = Total_Bonus × (Kontribusi_Anggota / Total_Kontribusi)",
          formulaEn: "Individual_Share = Total_Pool × (Member_Contribution / Total_Contribution)",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Hadiah bersama menciptakan rasa kepemilikan kolektif terhadap kesuksesan perusahaan.",
          tipEn: "Shared bonus pools cultivate collective ownership and celebration around company milestones."
        },
        {
          id: "squad",
          name: "Divisi / Unit Kerja",
          nameEn: "Division / Work Unit",
          english: "Organizational Work Units",
          category: "org",
          icon: "mdi-shield-check",
          impact: "system",
          impactLabel: "Struktur Organisasi",
          impactLabelEn: "Organizational Unit",
          definition: "Grup atau unit kerja internal (seperti Divisi Rekayasa, Penjualan, Pemasaran, SDM) tempat anggota berkolaborasi dan berkompetisi secara sehat di papan peringkat divisi.",
          definitionEn: "Functional organizational teams or divisions (e.g. Engineering, Sales, Customer Success) collaborating on division leaderboards.",
          workplaceExample: "Divisi 'Rekayasa Produk' dan 'Penjualan Korporat' berlomba meraih predikat Divisi Paling Produktif bulan ini.",
          workplaceExampleEn: "The 'Product Engineering' division competes with 'Enterprise Sales' for the Most Productive Division award.",
          formula: "Skor_Divisi = Σ(EXP_Anggota) / Jumlah_Anggota_Divisi",
          formulaEn: "Division_Score = Σ(Member_EXP) / Total_Division_Members",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Gunakan rata-rata terukur agar kompetisi antar divisi tetap sehat dan berimbang.",
          tipEn: "Balance division sizes or use normalized averages to maintain fair and motivating division competitions."
        },
        {
          id: "leaderboard",
          name: "Papan Peringkat",
          nameEn: "Leaderboard (Rankings)",
          english: "Real-time Rankings",
          category: "org",
          icon: "mdi-trophy",
          impact: "positive",
          impactLabel: "Kompetisi Sehat",
          impactLabelEn: "Healthy Competition",
          definition: "Papan peringkat waktu nyata yang memperlihatkan urutan capaian karyawan dan divisi berdasarkan perolehan EXP, Gold, dan kontribusi target.",
          definitionEn: "Real-time performance boards ranking individuals and divisions based on cumulative EXP, Gold, and Shared Target contributions.",
          workplaceExample: "Peringkat 1 hingga 3 bulanan di papan peringkat individu mendapatkan piagam penghargaan dan ekstra voucher makan malam.",
          workplaceExampleEn: "Top 3 individuals on the monthly leaderboard receive recognition badges and dining vouchers.",
          formula: "Urutan_Peringkat = URUTKAN BERDASARKAN total_xp TURUN, total_gold TURUN",
          formulaEn: "Rank_Sorting = ORDER BY total_xp DESC, total_gold DESC",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Lihat posisi Anda setiap pekan dan tantang diri Anda untuk masuk ke jajaran 5 Besar!",
          tipEn: "Check your standings weekly and challenge yourself to reach the Top 5 tier."
        },
        {
          id: "kudos_lounge",
          name: "Ruang Apresiasi (Kudos)",
          nameEn: "Kudos Lounge",
          english: "Peer-to-Peer Recognition",
          category: "org",
          icon: "mdi-message-star",
          impact: "positive",
          impactLabel: "Apresiasi Rekan",
          impactLabelEn: "Peer Recognition",
          definition: "Ruang apresiasi sosial antar rekan kerja di mana setiap anggota dapat mengirimkan ucapan terima kasih dan bonus koin emas kepada sesama kolega.",
          definitionEn: "A social recognition hub allowing peers to send heartfelt appreciation notes and micro-gold allocations to colleagues.",
          workplaceExample: "'Terima kasih Ahmad sudah membantu rilis server jam 8 malam!' disertai pemberian 10 Gold dari kuota bulanan.",
          workplaceExampleEn: "'Thank you Sarah for resolving the client outage at 8 PM!' accompanied by 10 Gold from the sender's allowance.",
          formula: "Kuota_Apresiasi_Bulanan = 50 Gold / Karyawan (Diatur ulang setiap awal bulan)",
          formulaEn: "Monthly_Kudos_Quota = 50 Gold / Employee (Resets monthly)",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Kirimkan apresiasi kepada rekan kerja yang berjasa; apresiasi kecil berdampak besar bagi motivasi tim!",
          tipEn: "Take a moment to recognize helpful teammates; micro-appreciation builds lasting morale."
        },
        {
          id: "seat_quota",
          name: "Kapasitas Kursi Lisensi",
          nameEn: "Seat Quota (License Capacity)",
          english: "Active License Seats",
          category: "billing",
          icon: "mdi-seat-outline",
          impact: "system",
          impactLabel: "Kapasitas Lisensi",
          impactLabelEn: "License Capacity",
          definition: "Jumlah maksimum akun karyawan yang dapat aktif bekerja dan menyelesaikan misi di dalam ruang kerja perusahaan secara bersamaan.",
          definitionEn: "The maximum number of active member accounts permitted to log in, complete tasks, and earn rewards concurrently in an enterprise workspace.",
          workplaceExample: "Perusahaan membeli lisensi Growth dengan kapasitas 50 kursi; 48 kursi telah terpakai oleh karyawan aktif.",
          workplaceExampleEn: "A company subscribes to a Growth plan with 50 member seats; 48 seats are currently assigned to active team members.",
          formula: "Sisa_Kursi = Total_Kursi_Dibeli - Total_Pengguna_Aktif",
          formulaEn: "Remaining_Seats = Total_Purchased_Seats - Total_Active_Users",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Jika kuota kursi hampir penuh, segera pesan kuota tambahan melalui menu Tagihan & Kuota agar karyawan baru langsung bisa bergabung.",
          tipEn: "When approaching maximum capacity, purchase add-on seats in the Billing & Quota menu so new hires can onboard seamlessly."
        },
        {
          id: "addon_quota",
          name: "Tambahan Kuota Kursi Eceran",
          nameEn: "Add-on Seat Quota",
          english: "On-demand License Expansion",
          category: "billing",
          icon: "mdi-plus-box",
          impact: "system",
          impactLabel: "Ekspansi Instan",
          impactLabelEn: "Instant Expansion",
          definition: "Fitur pembelian kuota kursi karyawan tambahan secara eceran per orang tanpa harus melakukan peningkatan paket tahunan berskala besar.",
          definitionEn: "An on-demand capability to expand workspace seat limits per user without altering existing base subscription tiers.",
          workplaceExample: "Menambah 5 karyawan magang di tengah bulan cukup dengan membeli 5 kursi eceran seharga Rp 15.000/kursi.",
          workplaceExampleEn: "Onboarding 5 seasonal interns by purchasing 5 add-on seats at IDR 15,000 per seat per month.",
          formula: "Biaya_Tambahan = Jumlah_Kursi × Harga_Satuan × Durasi_Bulan",
          formulaEn: "Addon_Cost = Seat_Count × Unit_Price × Duration_Months",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Sangat fleksibel untuk perusahaan yang memiliki karyawan musiman, pekerja lepas, atau peserta magang.",
          tipEn: "Ideal for agile organizations managing seasonal hires, contractors, or project interns."
        },
        {
          id: "midtrans",
          name: "Gerbang Pembayaran Otomatis",
          nameEn: "Automated Payment Gateway",
          english: "Automated Instant Checkout",
          category: "billing",
          icon: "mdi-credit-card-check",
          impact: "system",
          impactLabel: "Otomasi Transaksi",
          impactLabelEn: "Automated Checkout",
          definition: "Kanal pembayaran otomatis (QRIS, Virtual Account BCA/Mandiri/BNI, Kartu Kredit) yang langsung memverifikasi transaksi dan mengaktifkan kuota kursi seketika.",
          definitionEn: "Automated payment processing (QRIS, Virtual Accounts, Credit Cards) with real-time webhooks for instantaneous license activation.",
          workplaceExample: "Pemilik memindai QRIS lewat aplikasi perbankan seluler; dalam 3 detik status faktur berubah menjadi 'Lunas' dan kuota bertambah.",
          workplaceExampleEn: "An owner scans a dynamic QRIS code via mobile banking; invoice status flips to 'Paid' and seats unlock in 3 seconds.",
          formula: "N/A",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Gunakan pembayaran otomatis agar tidak perlu menunggu verifikasi transfer manual oleh admin.",
          tipEn: "Use instant payment methods to bypass manual bank transfer verification queues."
        },
        {
          id: "game_owner",
          name: "Pemilik Ruang Kerja",
          nameEn: "Workspace Owner",
          english: "Enterprise Executive & Sponsor",
          category: "role",
          icon: "mdi-crown",
          impact: "system",
          impactLabel: "Peran Eksekutif",
          impactLabelEn: "Executive Role",
          definition: "Pemilik akun perusahaan (Direktur, CEO, atau VP HR) yang memiliki wewenang penuh atas pembayaran paket, alokasi anggaran hadiah, dan akses analitik makro.",
          definitionEn: "The corporate executive account (CEO, Founder, Head of People) holding complete authority over licensing, budget allocations, and macro analytics.",
          workplaceExample: "Alexander Pratama mengelola ruang kerja PT Solusi Jaya, menentukan anggaran gold bulanan sebesar Rp 10 Juta, dan memantau ROI tim.",
          workplaceExampleEn: "The CEO sets the monthly reward pool liability, configures macro performance rules, and evaluates ROI in Executive Analytics.",
          formula: "N/A",
          targetRole: "Pemilik Ruang Kerja",
          targetRoleEn: "Workspace Owner",
          tip: "Jadikan Gamiteam sebagai dasbor budaya perusahaan untuk melihat performa tanpa perlu pengawasan mikro.",
          tipEn: "Leverage Gamiteam as your cultural cockpit to monitor organizational velocity without micromanagement."
        },
        {
          id: "game_maker",
          name: "Manajer Program",
          nameEn: "Program Manager",
          english: "Performance Architect & Operations",
          category: "role",
          icon: "mdi-hammer-wrench",
          impact: "system",
          impactLabel: "Peran Operasional",
          impactLabelEn: "Operational Role",
          definition: "Pengelola operasional (Manajer HR, Ketua Tim, atau Manajer Produk) yang bertugas merancang misi kerja, aturan tata tertib SOP, dan memverifikasi hasil kerja anggota.",
          definitionEn: "Operational managers (HR Leaders, Division Heads) tasked with crafting tasks, managing SOP consequences, and reviewing deliverables.",
          workplaceExample: "Rendra Wibowo membuat 12 misi sprint untuk tim Rekayasa dan memeriksa bukti pengajuan setiap sore.",
          workplaceExampleEn: "A team lead crafts 12 sprint tasks, configures OCR guidelines, and conducts end-of-day proof verification.",
          formula: "N/A",
          targetRole: "Manajer Program",
          targetRoleEn: "Program Manager",
          tip: "Kombinasikan misi harian jangka pendek dengan target kuartalan jangka panjang agar motivasi tim tetap terjaga.",
          tipEn: "Blend short-term daily habit loops with quarterly milestone tasks to sustain team drive year-round."
        },
        {
          id: "game_player",
          name: "Anggota Tim",
          nameEn: "Team Member",
          english: "Employee & Contributor",
          category: "role",
          icon: "mdi-account-star",
          impact: "system",
          impactLabel: "Peran Karyawan",
          impactLabelEn: "Employee Role",
          definition: "Karyawan atau anggota tim yang masuk setiap hari untuk melihat misi kerja, mengirimkan bukti pekerjaan, berkontribusi pada target bersama, dan menukarkan koin emas.",
          definitionEn: "Team members who log in to execute daily objectives, upload verifiable deliverables, contribute to quarterly targets, and redeem rewards.",
          workplaceExample: "Dimas Anggoro mengumpulkan 400 Gold dari menyelesaikan tiket perbaikan bug dan menukarnya dengan voucher kopi.",
          workplaceExampleEn: "A designer earns 400 Gold across two sprints, trading it in for workplace coffee vouchers.",
          formula: "N/A",
          targetRole: "Anggota Tim",
          targetRoleEn: "Team Member",
          tip: "Selesaikan misi harian di awal jam kerja untuk mengamankan poin dan rentetan harian Anda!",
          tipEn: "Complete key habit tasks early in your workday to secure bonus streaks and team leaderboard points."
        },
        {
          id: "superadmin",
          name: "Operator Platform (Super Admin)",
          nameEn: "Platform Operator (Super Admin)",
          english: "Platform Operator & Master Admin",
          category: "role",
          icon: "mdi-shield-check",
          impact: "system",
          impactLabel: "Peran Platform",
          impactLabelEn: "Platform Operator",
          definition: "Pengelola pusat seluruh platform SaaS Gamiteam yang mengontrol lisensi global, orientasi perusahaan mitra, verifikasi transfer bank, dan audit integritas sistem.",
          definitionEn: "The master platform operator overseeing global multi-tenant workspaces, bank transfer approvals, audit logging, and infrastructure health.",
          workplaceExample: "Mengonfirmasi faktur pembayaran manual dari mitra baru dan memastikan kesehatan basis data serta server awan.",
          workplaceExampleEn: "Verifying manual invoice wire transfers for new enterprise clients and maintaining platform integrity.",
          formula: "N/A",
          targetRole: "Super Admin",
          targetRoleEn: "Super Admin",
          tip: "Selalu tinjau Prospek Masuk secara berkala untuk menjaga rasio konversi mitra baru tetap tinggi.",
          tipEn: "Regularly audit inbound leads to maintain healthy enterprise pipeline conversion rates."
        }
      ],

      faqs: [
        {
          qId: "Apakah koin emas (Gold) bisa diubah menjadi uang tunai langsung?",
          qEn: "Can virtual Gold Coins be converted directly to cash?",
          aId: "Tergantung kebijakan Pemilik Ruang Kerja di masing-masing perusahaan. Sebagian besar perusahaan menyediakan katalog hadiah berupa voucher dompet digital (GoPay, OVO, ShopeePay), voucher belanja, perlengkapan kantor, atau fasilitas tambahan seperti cuti setengah hari.",
          aEn: "This depends on company policy configured by your Workspace Owner. Most enterprises offer digital rewards including e-wallet top-ups (GoPay, OVO), retail vouchers, branded merchandise, or office perks such as half-day leaves."
        },
        {
          qId: "Apa yang terjadi jika karyawan terkena penalti poin (Sanksi SOP)?",
          qEn: "What happens when an employee receives an SOP point deduction?",
          aId: "Saldo koin emas karyawan akan berkurang sesuai nominal sanksi yang ditetapkan. Namun, karyawan tidak perlu khawatir karena mereka dapat mengambil Misi Kompensasi (Pemulihan Penalti & Reputasi Poin) untuk mengembalikan poin yang hilang dengan menyelesaikan tugas perbaikan ekstra.",
          aEn: "The employee's gold balance decreases by the stipulated sanction. However, team members can undertake restorative Recovery Tasks (Compensation Objectives) to recoup deducted points through extra contributions."
        },
        {
          qId: "Bagaimana cara kerja OCR AI Gemini Vision dalam verifikasi bukti kerja?",
          qEn: "How does Gemini AI Vision OCR verify proof of work submissions?",
          aId: "Saat karyawan mengunggah foto atau tangkapan layar hasil kerja, model AI Gemini Vision akan memindai teks pada gambar, memvalidasi apakah tanggal, nominal, dan isi dokumen sesuai dengan kriteria misi. Jika cocok, sistem dapat langsung menyetujui klaim tanpa menunggu persetujuan manual dari manajer.",
          aEn: "Upon deliverable upload, the Gemini Vision AI model extracts textual telemetry (dates, invoice numbers, sign-offs) against the task rubric. If confidence criteria match, the system approves the submission automatically without manual review."
        },
        {
          qId: "Kapan Target Bersama Kuartalan diatur ulang?",
          qEn: "When does the Quarterly Shared Target cycle reset?",
          aId: "Target Bersama biasanya berlangsung selama 1 kuartal (3 bulan) atau sesuai jadwal yang diatur oleh Pemilik Ruang Kerja. Ketika target berhasil dicapai, seluruh anggota yang berpartisipasi mendapatkan porsi dari Dana Bonus Kolektif, dan target baru akan dimulai pada periode berikutnya.",
          aEn: "Shared Targets run on quarterly 90-day cycles configured by the Workspace Owner. Achieving the target unlocks the Shared Bonus Pool for all contributors, followed by a new milestone cycle for the upcoming quarter."
        },
        {
          qId: "Bagaimana jika perusahaan ingin menambah karyawan di tengah masa langganan?",
          qEn: "How do enterprises add seats mid-subscription?",
          aId: "Pemilik Ruang Kerja dapat menggunakan fitur 'Tambahan Kuota Kursi Eceran' di menu Tagihan & Kuota. Pembelian dapat dilakukan per kursi dan langsung aktif seketika setelah pembayaran via QRIS atau Virtual Account.",
          aEn: "Workspace Owners can use the 'Add-on Seat Quota' feature in the Billing & Quota section. Additional seats can be provisioned instantly per user with automated payment gateways."
        }
      ]
    };
  },
  computed: {
    isEn() {
      return this.$currentLocale === "en";
    },

    displayedCategories() {
      return this.categories.map(c => ({
        id: c.id,
        label: this.isEn ? c.labelEn : c.labelId
      }));
    },

    currentWorkflowData() {
      return this.roleWorkflows[this.selectedRoleWorkflow] || this.roleWorkflows.owner;
    },

    filteredTerms() {
      return this.terms.filter(term => {
        const matchesCategory =
          this.selectedCategory === "all" || term.category === this.selectedCategory;

        if (!matchesCategory) return false;

        if (!this.searchQuery) return true;

        const q = this.searchQuery.toLowerCase().trim();
        const nameToSearch = this.isEn ? (term.nameEn || term.name) : term.name;
        const defToSearch = this.isEn ? (term.definitionEn || term.definition) : term.definition;
        const exToSearch = this.isEn ? (term.workplaceExampleEn || term.workplaceExample) : term.workplaceExample;
        const tipToSearch = this.isEn ? (term.tipEn || term.tip) : term.tip;

        return (
          nameToSearch.toLowerCase().includes(q) ||
          term.name.toLowerCase().includes(q) ||
          (term.english && term.english.toLowerCase().includes(q)) ||
          defToSearch.toLowerCase().includes(q) ||
          exToSearch.toLowerCase().includes(q) ||
          (term.formula && term.formula.toLowerCase().includes(q)) ||
          tipToSearch.toLowerCase().includes(q)
        );
      });
    },

    calculatedLevel() {
      return Math.floor((this.calcExp || 0) / 100) + 1;
    },

    currentTierName() {
      const lv = this.calculatedLevel;
      if (lv >= 50) return this.isEn ? "Principal" : "Eksekutif Utama";
      if (lv >= 30) return this.isEn ? "Senior Specialist" : "Spesialis Senior";
      if (lv >= 20) return this.isEn ? "Lead Contributor" : "Kontributor Ahli";
      if (lv >= 10) return this.isEn ? "Core Member" : "Anggota Inti";
      if (lv >= 5) return this.isEn ? "Junior Specialist" : "Spesialis Muda";
      return this.isEn ? "Associate" : "Anggota Muda";
    },

    calculatedStreakMultiplier() {
      const s = Math.min(Math.max(this.calcStreak || 1, 1), 30);
      return (1.0 + s * 0.05).toFixed(2);
    },

    calculatedBossDamage() {
      const exp = Number(this.calcExp) || 0;
      const gold = Number(this.calcGoldEarned) || 0;
      const mult = Number(this.calculatedStreakMultiplier);
      return Math.round((exp + gold * 2) * mult);
    },

    calculatedBurnRate() {
      const earned = Number(this.calcGoldEarned) || 1;
      const redeemed = Number(this.calcGoldRedeemed) || 0;
      return Math.min(100, Math.round((redeemed / earned) * 100));
    },

    burnRateStatus() {
      const rate = this.calculatedBurnRate;
      if (rate < 30) return this.isEn ? "Hoarded / Low Activity" : "Poin Tertimbun / Rendah";
      if (rate <= 75) return this.isEn ? "Healthy & Stable Liquidity" : "Likuiditas Sehat & Stabil";
      return this.isEn ? "High / Liability Risk" : "Tinggi / Perlu Kontrol Liabilitas";
    },

    burnRateColor() {
      const rate = this.calculatedBurnRate;
      if (rate < 30) return "text-info";
      if (rate <= 75) return "text-success font-weight-bold";
      return "text-warning font-weight-bold";
    }
  },
  methods: {
    getCategoryLabel(catId) {
      const found = this.categories.find(c => c.id === catId);
      if (!found) return catId;
      return this.isEn ? found.labelEn : found.labelId;
    },

    getCategoryBadgeClass(catId) {
      switch (catId) {
        case "currency":
          return "badge-outline-warning text-warning";
        case "quest":
          return "badge-outline-success text-success";
        case "discipline":
          return "badge-outline-danger text-danger";
        case "raid":
          return "badge-outline-primary text-primary";
        case "org":
          return "badge-outline-info text-info";
        case "role":
          return "badge-outline-light text-light";
        case "billing":
          return "badge-outline-warning text-white";
        default:
          return "badge-outline-secondary";
      }
    },

    getImpactBadgeClass(impact) {
      switch (impact) {
        case "positive":
          return "badge-success text-dark font-weight-bold";
        case "negative":
          return "badge-danger text-white font-weight-bold";
        case "system":
          return "badge-secondary text-light";
        default:
          return "badge-info text-dark";
      }
    },

    getImpactIcon(impact) {
      switch (impact) {
        case "positive":
          return "mdi mdi-arrow-up-bold-circle";
        case "negative":
          return "mdi mdi-arrow-down-bold-circle";
        case "system":
          return "mdi mdi-cog-outline";
        default:
          return "mdi mdi-information-outline";
      }
    },

    toggleFaq(index) {
      this.expandedFaq = this.expandedFaq === index ? null : index;
    },

    printOrExport() {
      window.print();
    }
  }
};
</script>

<style scoped>
.glossary-view {
  font-family: inherit;
}

.custom-pills .nav-link {
  color: #94a3b8;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.custom-pills .nav-link.active {
  background-color: #3b82f6;
  color: #ffffff;
}

.custom-pills .nav-link:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #f8fafc;
}

.term-card {
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.term-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
  border-color: #3b82f6 !important;
}

.border-left-warning {
  border-left: 4px solid #ff9900 !important;
}

.border-left-success {
  border-left: 4px solid #10b981 !important;
}

.border-left-info {
  border-left: 4px solid #06b6d4 !important;
}

.border-left-danger {
  border-left: 4px solid #ef4444 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.font-monospace {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
}

@media print {
  .nav-pills,
  .btn,
  .input-group,
  .sidebar,
  .navbar {
    display: none !important;
  }
  .card {
    background: #ffffff !important;
    color: #000000 !important;
    border: 1px solid #cccccc !important;
  }
  .text-white, .text-light, .text-muted {
    color: #111827 !important;
  }
}
</style>
